import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { lazy, Suspense, useEffect } from "react";

// Import Home directly to render immediately for LCP optimization
// Privacy page is lazy loaded since it's not critical for initial render
import Home from "./pages/Home";
const Privacy = lazy(() => import("./pages/Privacy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SeoParaAdvogados = lazy(() => import("./pages/services/SeoParaAdvogados"));
const TrafegoPagoParaAdvogados = lazy(() => import("./pages/services/TrafegoPagoParaAdvogados"));
const SitesParaAdvogados = lazy(() => import("./pages/services/SitesParaAdvogados"));
const LandingPagesParaAdvogados = lazy(() => import("./pages/services/LandingPagesParaAdvogados"));
const MarketingDeConteudoParaAdvogados = lazy(() => import("./pages/services/MarketingDeConteudoParaAdvogados"));
const CrmParaAdvogados = lazy(() => import("./pages/services/CrmParaAdvogados"));
const GuiaMarketingJuridicoDigital = lazy(() => import("./pages/evergreen/GuiaMarketingJuridicoDigital"));
const Servicos = lazy(() => import("./pages/Servicos"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback component
const LoadingFallback = () => <div style={{ minHeight: "100vh" }} />;

// Wrapper component para redirecionar URLs antigas com /artigo/:slug
function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/blog/${slug}` : '/blog'} replace />;
}

// Wrapper component para BlogArticle que detecta se é artigo ou categoria
function BlogArticleWrapper() {
  const { slug } = useParams<{ slug?: string }>();
  const categorySlugs = ['seo', 'trafego-pago', 'marketing-conteudo', 'ia', 'landing-pages', 'redes-sociais', 'sites'];

  // Categorias continuam usando a listagem; qualquer outro slug é resolvido pelo BlogArticle,
  // que exibe o artigo correspondente ou um 404 real quando o slug não existe.
  if (slug && categorySlugs.includes(slug)) {
    return <Blog />;
  }

  return slug ? <BlogArticle /> : <Blog />;
}

function Router() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/politica-de-privacidade" element={<Privacy />} />
      <Route path="/privacy" element={<Navigate to="/politica-de-privacidade" replace />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/404" element={<NotFound />} />
      {/* Redirecionamento 301 para URL antiga do artigo */}
      <Route path="/blog/artigo/google-meu-negocio-advogados" element={<Navigate to="/blog/google-meu-negocio-advogados-2024" replace />} />
      <Route path="/blog/artigo/google-meu-negocio-advogados-2024" element={<Navigate to="/blog/google-meu-negocio-advogados-2024" replace />} />
      {/* Redirecionamentos de slugs antigos para os novos endereços canônicos */}
      <Route path="/blog/redes-sociais-advogados" element={<Navigate to="/blog/redes-sociais-para-advogados-o-que-postar-sem-infringir-o-codigo-de-etica" replace />} />
      <Route path="/blog/landing-pages-conversao" element={<Navigate to="/servicos/landing-pages-para-advogados" replace />} />
      <Route path="/blog/google-ads-para-advogados-vale-a-pena-investir-em-2026" element={<Navigate to="/blog/google-ads-para-advogados-leilao-cpc-roi-e-erros-de-configuracao" replace />} />
      <Route path="/blog/testes-ab-landing-pages" element={<Navigate to="/servicos/landing-pages-para-advogados" replace />} />
      <Route path="/blog/marketing-conteudo-estrategias" element={<Navigate to="/blog/conteudo-juridico-que-converte" replace />} />
      <Route path="/blog/seo-on-page-off-page" element={<Navigate to="/blog/seo-juridico-escritorio" replace />} />
      <Route path="/blog/investir-seo-2026" element={<Navigate to="/blog/seo-para-advogados-primeira-pagina-google" replace />} />
      <Route path="/blog/melhor-horario-anuncios" element={<Navigate to="/blog/google-ads-para-advogados-leilao-cpc-roi-e-erros-de-configuracao" replace />} />
      <Route path="/blog/redes-sociais-para-advogados-o-que-postar-com-etica" element={<Navigate to="/blog/redes-sociais-para-advogados-o-que-postar-sem-infringir-o-codigo-de-etica" replace />} />
      <Route path="/blog/captacao-clientes-advogados-trafego-pago" element={<Navigate to="/blog/captacao-clientes-advogados-trafego-pago-com-etica-e-estrategia" replace />} />
      <Route path="/blog/link-building-juridico" element={<Navigate to="/blog/link-building-juridico-como-construir-autoridade-e-ranquear-com-etica" replace />} />
      {/* Redirecionamento para URLs antigas com /artigo */}
      <Route path="/blog/artigo/:slug" element={<LegacyBlogRedirect />} />
      {/* Rota padrão do blog */}
      <Route path="/blog" element={<Blog />} />
      {/* Rota específica para artigos do blog */}
      <Route path="/blog/:slug" element={<BlogArticleWrapper />} />
      {/* Rota de categorias */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/servicos/seo-para-advogados" element={<SeoParaAdvogados />} />
      <Route path="/servicos/trafego-pago-para-advogados" element={<TrafegoPagoParaAdvogados />} />
      <Route path="/servicos/sites-para-advogados" element={<SitesParaAdvogados />} />
      <Route path="/servicos/landing-pages-para-advogados" element={<LandingPagesParaAdvogados />} />
      <Route path="/servicos/marketing-de-conteudo-para-advogados" element={<MarketingDeConteudoParaAdvogados />} />
      <Route path="/servicos/crm-para-advogados" element={<CrmParaAdvogados />} />
      <Route path="/servicos" element={<Servicos />} />
      <Route path="/guia-marketing-juridico-digital" element={<GuiaMarketingJuridicoDigital />} />
      {/* Final fallback route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Wrap Router with Suspense for lazy loaded components
function RouterWithSuspense() {
  return (
    <main>
      <Suspense fallback={<LoadingFallback />}>
        <Router />
      </Suspense>
    </main>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook
// Deploy fix: Force auto-publish

function App() {
  useEffect(() => {
    // Handle anchor links when page loads or hash changes
    const handleAnchorScroll = () => {
      const hash = window.location.hash;
      if (hash) {
        // Remove the # from hash
        const id = hash.substring(1);
        // Wait for DOM to be ready
        setTimeout(() => {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Handle on initial load
    handleAnchorScroll();

    // Handle on hash change
    window.addEventListener('hashchange', handleAnchorScroll);
    return () => window.removeEventListener('hashchange', handleAnchorScroll);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <BrowserRouter>
          <RouterWithSuspense />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
